/*
 * Ryan O'Dowd
 * 2024-07-15
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import Banner from '@components/common/Banner';
import Button from '@components/common/Button';
import Checkbox from '@components/common/Checkbox';
import Dialog from '@components/common/Dialog';
import Table from '@components/common/Table';
import Tooltip from '@components/common/Tooltip';
import {
  apiUpdateClassroomGoal,
} from '@utils/store/actions';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';

const PublishButton = (props: PublishButtonProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const activeClassroomStudents = hooks.useTypedSelector((state) => state.classroomStudents[props.classroomId] || []).filter((student) => student.status === 'active');

  const [_isGoalAssignmentDialogOpen, setIsGoalAssignmentDialogOpen] = useState(false);
  const [_userIdsToAssign, setUserIdsToAssign] = useState(activeClassroomStudents.map((cs) => cs.user?.id).filter((id) => id)); // @TODO: shouldn't need to null check this

  let checkAllStatus = null;
  if (_userIdsToAssign.length === activeClassroomStudents.length) {
    checkAllStatus = true;
  } else if (_userIdsToAssign.length === 0) {
    checkAllStatus = false;
  }

  const studentTableRows = activeClassroomStudents.map((cs) => {
    return [ // @TODO: update ids
      {
        id: `${cs.id}_checkbox`,
        content: (
          <Checkbox
            isChecked={_userIdsToAssign.includes(cs.user.id)}
            onPress={() => {
              setUserIdsToAssign((curr) => {
                if (curr.includes(cs.user.id)) {
                  return curr.filter((id) => id !== cs.user.id);
                }

                return [...curr, cs.user.id];
              });
            }}
          />
        ),
        sortValue: cs.user ? cs.user.display_name?.toLocaleLowerCase() : cs.display_name?.toLocaleLowerCase(),
      },
      {
        id: `${cs.id}_display_name`,
        content: (
          <span style={styles.checkboxAndName}>
            {/* @TODO: default all to be checked */}
            {cs.user ? cs.user.display_name : cs.display_name}{/* @TODO: if user exists, show cs.display_name on hover for convenience? */}
          </span>
        ),
        sortValue: cs.user ? cs.user.display_name?.toLocaleLowerCase() : cs.display_name?.toLocaleLowerCase(),
      }, // @TODO: maybe surround with join date (or invitation date if pending)
      /* @TODO:
      {
        id: `${cs.id}_date_invited`,
        content: <Tooltip title={dateFnsFormat(cs.created_at, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(cs.created_at, 'MMM d')}</span></Tooltip>,
        sortValue: new Date(cs.created_at || 0).getTime(),
      },
      */
      {
        id: `${cs.id}_date_joined`,
        content: cs.date_joined ? <Tooltip title={dateFnsFormat(cs.date_joined, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(cs.date_joined, 'MMM d')}</span></Tooltip> : '-',
        sortValue: new Date(cs.date_joined || 0).getTime(),
      },
    ];
  });

  return (
    <React.Fragment>
      <Button
        theme={styles.colors.accent}
        onPress={() => {
          setIsGoalAssignmentDialogOpen(true);
        }}
      >
        Publish
      </Button>
      <Dialog
        visible={_isGoalAssignmentDialogOpen}
        onDismiss={() => setIsGoalAssignmentDialogOpen(false)}
      >
        <Dialog.Title>Assign goal to students</Dialog.Title>{/* @TODO: i18n */}
        <Dialog.Content>
          <span>{/* @TODO: shouldn't need this span, but without it, there's no vertical padding in Banner */}
            <Banner
              type='warning'
              text='Ready to publish this goal?  Once published, assignments will be locked, and students will be granted access.  Most assignment settings will remain editable, but passages will be locked.'
            />
          </span>
          <br />
          <Table// @TODO: rows shouldn't have pointer on hover unless they're selectable
            // @TODO: shouldn't be accordion
            tableTitle='Active students'
            open={true}// @TODO: default to false, but have badge for notifications if there are students to approve, for example
            headerRowData={[
              {
                width: 40,
                id: 'checkbox', // @TODO: would be nice for this to be a prop instead of rebuilding it each time
                content: (
                  <Checkbox
                    isChecked={checkAllStatus}
                    onPress={() => {
                      if (_userIdsToAssign.length > 0 && _userIdsToAssign.length <= activeClassroomStudents.length) {
                        setUserIdsToAssign([]);
                      } else {
                        setUserIdsToAssign(activeClassroomStudents.map((cs) => cs.user.id));
                      }
                    }}
                  />
                ),
              },
              {width: 350, id: 'name', content: 'Name'},
              // @TODO: 'Date invited',
              {width: 100, id: 'date_joined', content: 'Date joined', rightAlign: true},
            ]}// @TODO: some of these should be named better and/or have tooltips
            isSortable={true}
            orderByIndex={1}
            orderDirection='ascending'
            rowData={studentTableRows}
            // @TODO: onRowPress={(row) => setSelectedStudentId(+row[0].id.split('_')[0])}// @TODO: way too hacky
          />
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='text'
            onPress={() => setIsGoalAssignmentDialogOpen(false)}
          >
            {i18n('cancel')}
          </Button>
          <Button onPress={() => dispatch(apiUpdateClassroomGoal(props.classroomId, props.goalId, {status: 'published'}, _userIdsToAssign))}>
            Publish{_userIdsToAssign.length ? ' and assign' : ''}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </React.Fragment>
  );
};

class PublishButtonProps {
  classroomId: number;
  goalId: number;
};

export default PublishButton;
